import { CTBProgram, ProgramType } from "../api/endpoints/programs/parsePrograms";

/**
 * Determines if a program is in "Draft" status.
 * A program is a draft if it is not activated and is missing any required fields.
 */
export const isProgramDraft = (program: CTBProgram | null | undefined): boolean => {
  if (!program) return false;
  
  // Legacy V1 programs are never considered drafts in the new status system
  if ((program.architecture_version || 1) < 2) return false;
  
  // If already activated or in a phase beyond draft, it's not a draft
  if (program.isActivated || 
      program.phase_status === 'active_reporting' || 
      program.phase_status === 'pending_auth') return false;
  
  // If the raw API object has a status field (some endpoints provide this)
  const status = (program as any).status;
  if (status && (status === 'draft' || status === 'Draft')) return true;
  if (status && status !== 'draft' && status !== 'Draft') return false;

  // If deleted, it's not considered a draft
  if (program.isDelete) return false;

  // Basic validation: A program MUST have a title to not be a draft
  if (!program.title?.trim() || program.title === 'No program available') return true;

  // Type-specific basic validation
  if (program.type === ProgramType.PTAAS) {
    // For PTaaS, an asset is crucial. If no asset_id, it's likely still in the wizard.
    if (!program.asset_id) return true;
    
    // Check if dates are specifically "Invalid date" or missing, but only for one_off programs
    if (program.scheduling_type === 'one_off' && (!program.expected_start_date || program.expected_start_date === "Invalid date" || 
        !program.expected_end_date || program.expected_end_date === "Invalid date")) {
      // If it's a one-off program with no dates, it's a draft
      return true;
    }
  }

  // If it has a title and passed basic type-specific checks, it's not a draft
  // We are more lenient here to avoid hiding launched programs that might have empty optional fields.
  return false;
};
/**
 * Checks if a program has any runs with unsubmitted scope snapshots.
 */
export const isScopeSnapshotPending = (program: CTBProgram | null | undefined): boolean => {
  if (!program || (program.architecture_version || 1) < 2) return false;
  return (program.unsubmittedScopeCount || 0) > 0;
};

/**
 * Checks if a program has any runs that have been submitted but NOT verified by admin.
 */
export const isScopePendingVerification = (program: CTBProgram | null | undefined): boolean => {
  if (!program || (program.architecture_version || 1) < 2) return false;
  return (program.submittedButUnverifiedCount || 0) > 0;
};
